// pages/DwiSendShopAdmin.jsx
// Admin → DwiSend → 🇨🇩 DRC shop (DwiSend Regional, 2026-10-27).
// Copy of Uganda's shop admin, in USD and paid offline:
//  - Orders: the client orders with delivery "to be confirmed"; agree the fee
//    (weight, town, items) and set it on the order — admins only. When the
//    money arrives, record it with proof here; a DIFFERENT approver approves it
//    (Approvals tab / /dwisend-approvals) and only then is the order paid. Then
//    mark it on its way and delivered.
//  - Products: name/description as DRC clients see them (French), sizes with
//    price, private cost in USD and weight (kg), stock, private supplier details.
//  - Settings: margin, rounding, payment window (48 h) and the longest hold (7 days).
// Admin screens stay English; the client sees French or English.
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { dwisendShopAPI, dwisendPaymentAPI, marketAPI } from '../services/api';
import { Button, Card, Input, Select, Badge, Modal, Table, Td, Empty, Spinner, SectionHeader } from '../components/ui';
import { useOrderAlerts } from '../context/OrderAlertsContext';
import { pinNew } from '../hooks/orderAlerts';

const STATUS = {
  awaiting_payment: ['Awaiting payment', 'amber'], paid: ['Paid (approved) — to send', 'teal'], dispatched: ['On its way', 'cyan'],
  delivered: ['Delivered', 'green'], cancelled: ['Cancelled', 'gray'], expired: ['Expired — not paid', 'gray'],
};
const FILTERS = [['active', 'Active'], ['fee_pending', 'Delivery fee to set'], ['awaiting_payment', 'Awaiting payment'], ['paid', 'Paid — to send'], ['', 'All']];
const usd = (minor, currency = 'USD') => (minor == null ? '—' : `${currency} ${(Number(minor) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const asText = minor => (minor == null ? '' : (minor / 100).toFixed(2));
const ugxText = n => `UGX ${Number(n || 0).toLocaleString('en-US')}`;
const rateText = r => Number(r).toLocaleString('en-US', { maximumFractionDigits: 2 });

// Stock is counted in the product's base unit (kg, piece, litre). Say it in
// sizes too: 500 kg → "500 kg = 50 × 10kg"; a part that fills no whole size
// shows as "+ 5 kg". sizes = [{ name, baseQty }] (MarketUnit shape).
export function stockEquivalent(qty, baseUnit, sizes = []) {
  const n = Number(qty);
  if (qty === '' || qty == null || !Number.isInteger(n) || n < 0) return '';
  const head = `${n.toLocaleString('en-US')} ${baseUnit || ''}`.trim();
  const seen = new Set();
  const parts = sizes.filter(u => u && u.baseQty > 1 && !seen.has(u.name) && seen.add(u.name)).map(u => {
    const whole = Math.floor(n / u.baseQty), rest = n - whole * u.baseQty;
    return `${whole.toLocaleString('en-US')} × ${u.name}${rest ? ` + ${rest.toLocaleString('en-US')} ${baseUnit}` : ''}`;
  });
  return parts.length ? `${head} = ${parts.join(' or ')}` : head;
}
// grams → "1,250 kg" / "2.15 t"
const kgText = g => (g >= 1000000 ? `${(g / 1000000).toLocaleString('en-US', { maximumFractionDigits: 2 })} t` : `${((g || 0) / 1000).toLocaleString('en-US', { maximumFractionDigits: 3 })} kg`);
// Product weights are optional (DwiSend weighs each package): an order's total is shown only when every item had one.
const weightOf = o => (o.weightComplete !== false && o.totalWeightGrams ? kgText(o.totalWeightGrams) : '');
const feePending = o => o.deliveryFeeMinor == null;
const when = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const textarea = 'mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan';

export function DrcShopSection() {
  const [tab, setTab] = useState('orders');
  return (
    <Card>
      <SectionHeader title="🇨🇩 DRC shop" />
      <p className="text-xs text-text-muted mb-3">
        DRC clients buy listed products in USD and pay offline with the order reference. Stock is held while they pay
        (Settings → payment window). An order becomes <strong>paid</strong> only when its payment is recorded here and approved by a
        different approver with their code. Uganda&apos;s shop is separate and unaffected.
      </p>
      <div className="flex gap-2 mb-3">
        {[['orders', 'Orders'], ['products', 'Products'], ['settings', 'Settings']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${tab === k ? 'bg-cyan/20 text-cyan' : 'text-text-muted hover:text-text-primary'}`}>{label}</button>
        ))}
      </div>
      {tab === 'orders' && <OrdersPanel />}
      {tab === 'products' && <ProductsPanel />}
      {tab === 'settings' && <SettingsPanel />}
    </Card>
  );
}

// ── Orders ───────────────────────────────────────────────────────────────────
function OrdersPanel() {
  const { newRefs, markSeen } = useOrderAlerts();
  const [filter, setFilter] = useState('active');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    try { setData(await dwisendShopAPI.adminOrders({ status: filter || undefined, q: q || undefined })); }
    catch (err) { toast.error(err.message); }
  }, [filter, q]);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      <div className="flex gap-2 flex-wrap items-end mb-3">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === k ? 'bg-cyan/20 text-cyan' : 'text-text-muted hover:text-text-primary'}`}>
            {label}{data?.counts && k && data.counts[k] ? ` (${data.counts[k]})` : ''}
          </button>
        ))}
        <Input placeholder="Search ref, phone, name…" value={q} onChange={e => setQ(e.target.value)} className="min-w-[220px]" />
      </div>
      {!data ? <div className="py-10 flex justify-center"><Spinner /></div>
        : data.orders.length === 0 ? <Empty title="No DRC shop orders here" description="Orders from DRC clients appear here." />
        : (
          <Table headers={['Ref', 'Client', 'Total', 'City', 'Status', 'Pay by', 'Placed']}>
            {pinNew(data.orders, newRefs, 'ref').map(o => (
              <tr key={o._id} className={newRefs.has(o.ref) ? 'cursor-pointer bg-red/10 hover:bg-bg-elevated' : 'cursor-pointer hover:bg-bg-elevated'} onClick={() => { setOpenId(o._id); markSeen(o.ref); }}>
                <Td className="font-mono text-cyan">{newRefs.has(o.ref) && <span className="mr-1 px-1.5 rounded bg-red text-white text-[10px] font-black font-sans">NEW</span>}{o.ref}</Td>
                <Td>{o.buyerName || '—'}<div className="text-[11px] text-text-muted">{o.phone}</div></Td>
                <Td className="text-text-primary">{feePending(o) ? <>{usd(o.subtotalMinor, o.currency)}<div className="text-[11px] text-amber">+ delivery to set</div></> : usd(o.totalMinor, o.currency)}</Td>
                <Td>{o.cityName}{weightOf(o) && <div className="text-[11px] text-text-muted">{weightOf(o)}</div>}</Td>
                <Td><Badge label={(STATUS[o.status] || [o.status])[0]} color={(STATUS[o.status] || [])[1] || 'gray'} />
                  {o.status === 'awaiting_payment' && feePending(o) && <div className="text-[11px] text-amber mt-0.5">Delivery fee to set</div>}
                  {o.payment && o.status === 'awaiting_payment' && <div className="text-[11px] text-cyan mt-0.5">{o.payment.ref} — waiting for approval</div>}</Td>
                <Td>{o.status === 'awaiting_payment' ? when(o.payBy) : '—'}</Td>
                <Td>{when(o.createdAt)}</Td>
              </tr>
            ))}
          </Table>
        )}
      {openId && <OrderModal id={openId} onClose={() => { setOpenId(null); load(); }} />}
    </>
  );
}

function OrderModal({ id, onClose }) {
  const [o, setO] = useState(null);
  const [notes, setNotes] = useState('');
  const [recording, setRecording] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fee, setFee] = useState('');
  const load = useCallback(async () => {
    try { const r = await dwisendShopAPI.adminOrder(id); setO(r.order); setNotes(r.order.adminNotes || ''); setFee(asText(r.order.deliveryFeeMinor)); }
    catch (err) { toast.error(err.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const move = async (status, ask) => {
    let reason;
    if (ask) { reason = window.prompt(ask); if (reason === null) return; }
    else if (!window.confirm(`Mark ${o.ref} as ${(STATUS[status] || [status])[0]}? The client is told.`)) return;
    setBusy(true);
    try { await dwisendShopAPI.adminOrderStatus(o._id, { status, reason }); toast.success('Saved'); load(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  const saveFee = async () => {
    const total = o.subtotalMinor + Math.round(Number(String(fee).replace(',', '.')) * 100);
    if (!window.confirm(`Set the delivery fee for ${o.ref} to ${o.currency} ${fee}? The client is told to pay ${Number.isFinite(total) ? usd(total, o.currency) : 'products + fee'} in total, with a new payment deadline (payment window from Settings).`)) return;
    setBusy(true);
    try { await dwisendShopAPI.adminDeliveryFee(o._id, fee); toast.success('Delivery fee saved — client told'); load(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  const saveNotes = async () => {
    try { await dwisendShopAPI.adminOrderNotes(o._id, notes); toast.success('Notes saved'); }
    catch (err) { toast.error(err.message); }
  };

  if (!o) return <Modal open onClose={onClose} title="DRC shop order"><div className="py-10 flex justify-center"><Spinner /></div></Modal>;
  const livePayment = o.payments.find(p => p.status !== 'rejected');
  const d = o.delivery || {};
  const Row = ({ k, v }) => <div className="flex gap-3 text-sm"><span className="w-32 shrink-0 text-text-muted">{k}</span><span className="text-text-primary">{v || '—'}</span></div>;
  return (
    <Modal open onClose={onClose} title={`${o.ref} — ${feePending(o) ? `${usd(o.subtotalMinor, o.currency)} + delivery to set` : usd(o.totalMinor, o.currency)}`} size="lg">
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge label={(STATUS[o.status] || [o.status])[0]} color={(STATUS[o.status] || [])[1] || 'gray'} />
          <span className="text-xs text-text-muted">Client language: {o.buyer?.language || (o.buyer?.countryCode === 'CD' ? 'fr (default)' : 'en')}</span>
        </div>
        <div className="space-y-1">
          <Row k="Client" v={`${o.buyer?.name || o.buyerName} · ${o.buyer?.email || ''} · ${o.buyer?.phone || ''}`} />
          <Row k="Deliver to" v={`${o.cityName} — ${o.deliveryAddress}`} />
          <Row k="Phone / WhatsApp" v={`${o.phone}${o.whatsapp ? ` · WA ${o.whatsapp}` : ''}`} />
          <Row k="Client notes" v={o.notes} />
          {weightOf(o) && <Row k="Total weight (from product weights)" v={weightOf(o)} />}
          {o.status === 'awaiting_payment' && <Row k="Stock held until" v={`${when(o.payBy)} (never later than ${when(o.holdLimitAt)})`} />}
          {o.status === 'cancelled' && <Row k="Cancel reason" v={o.cancelReason} />}
          {(d.driverName || d.driverPhone) && <Row k="Driver" v={[d.driverName, d.driverPhone, d.vehicle, d.plate].filter(Boolean).join(' · ') + (d.tripCostMinor != null ? ` · trip ${usd(d.tripCostMinor, o.currency)} (private)` : '')} />}
        </div>
        <div className="rounded-xl border border-border p-3 text-sm">
          {o.items.map((i, k) => (
            <div key={k} className="flex justify-between gap-3"><span>{i.qty} × {i.name} ({i.unitName}) @ {usd(i.unitPriceMinor, o.currency)}{i.costMinor != null ? ` · cost ${usd(i.costMinor, o.currency)}` : ''}
              {i.weightGrams != null && <span className="text-text-muted"> · {kgText(i.weightGrams * i.qty)}</span>}</span><span>{usd(i.lineTotalMinor, o.currency)}</span></div>
          ))}
          <div className="flex justify-between gap-3 text-text-muted"><span>Delivery ({o.cityName})</span><span>{feePending(o) ? 'to be confirmed' : usd(o.deliveryFeeMinor, o.currency)}</span></div>
          <div className="flex justify-between gap-3 font-bold text-text-primary border-t border-border pt-1 mt-1">
            <span>Total{o.marginMinor != null ? ` · margin ${usd(o.marginMinor, o.currency)}${o.marginComplete ? '' : ' (part)'}` : ''}</span><span>{usd(o.totalMinor, o.currency)}</span>
          </div>
        </div>

        {o.status === 'awaiting_payment' && !livePayment && (
          <div className="rounded-xl border border-cyan/40 bg-cyan/5 p-3 space-y-2">
            <div className="text-sm text-text-primary font-bold">Delivery fee ({o.currency}) — agreed with the client</div>
            <p className="text-xs text-text-muted">Weigh the package and enter the delivery fee. Products {usd(o.subtotalMinor, o.currency)}{weightOf(o) ? ` · ${weightOf(o)}` : ''} · to {o.cityName}. Saving tells the client the total to pay
              and restarts the payment window (never past {when(o.holdLimitAt)}). It can be changed until a payment is recorded. Admins only.</p>
            <div className="flex gap-2 items-end">
              <Input label="Delivery fee" inputMode="decimal" value={fee} onChange={e => setFee(e.target.value)} placeholder="e.g. 85 or 0" />
              <Button size="sm" loading={busy} disabled={String(fee).trim() === ''} onClick={saveFee}>{feePending(o) ? 'Set fee' : 'Change fee'}</Button>
            </div>
          </div>
        )}

        {o.status === 'awaiting_payment' && !feePending(o) && (
          <div className="rounded-xl border border-amber/50 bg-amber/5 p-3 space-y-2">
            <div className="text-sm text-text-primary font-bold">Waiting for payment: {usd(o.totalMinor, o.currency)} — reference {o.ref}</div>
            {!o.paymentInstructionsSet && <div className="text-xs text-amber">No payment details are set on the server (DWISEND_CD_PAY_1…6 in .env) — the client is told to wait for details.</div>}
            {livePayment
              ? <div className="text-sm">Payment <span className="font-mono">{livePayment.ref}</span> recorded — waiting for a different approver (Approvals tab). The order will not expire meanwhile, and the delivery fee is locked.</div>
              : <Button size="sm" onClick={() => setRecording(true)}>Record payment received…</Button>}
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          {o.status === 'paid' && <Button size="sm" loading={busy} onClick={() => setDispatching(true)}>Mark on its way…</Button>}
          {o.status === 'dispatched' && <Button size="sm" loading={busy} onClick={() => move('delivered')}>Mark delivered</Button>}
          {((o.status === 'awaiting_payment' && !livePayment) || o.status === 'paid') && (
            <Button size="sm" variant="ghost" loading={busy}
              onClick={() => move('cancelled', o.status === 'paid'
                ? 'Cancel a PAID order? The refund is done by hand outside the app. Why? (the client sees this — write it in their language)'
                : 'Why cancel? The client sees this text (write it in their language).')}>Cancel order…</Button>
          )}
        </div>

        {o.payments.length > 0 && (
          <div>
            <div className="text-sm font-bold text-text-primary mb-1">Payments</div>
            {o.payments.map(p => (
              <div key={p._id} className="text-xs text-text-secondary">
                <span className="font-mono">{p.ref}</span> · {usd(p.amountMinor, p.currency)} · {p.method} {p.providerRef} · <b>{p.status}</b>{p.rejectReason ? ` (${p.rejectReason})` : ''} · {when(p.createdAt)}
              </div>
            ))}
          </div>
        )}
        <div>
          <label className="text-xs font-semibold text-text-secondary">Admin notes (client never sees)</label>
          <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} className={textarea} />
          <Button size="xs" variant="secondary" onClick={saveNotes}>Save notes</Button>
        </div>
      </div>
      {recording && <RecordPaymentModal o={o} onClose={() => setRecording(false)} onDone={() => { setRecording(false); load(); }} />}
      {dispatching && <DispatchModal o={o} onClose={() => setDispatching(false)} onDone={() => { setDispatching(false); load(); }} />}
    </Modal>
  );
}

function DispatchModal({ o, onClose, onDone }) {
  const [f, setF] = useState({ driverName: '', driverPhone: '', vehicle: '', plate: '', tripCost: '', note: '' });
  const [saving, setSaving] = useState(false);
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try { await dwisendShopAPI.adminOrderStatus(o._id, { status: 'dispatched', delivery: f }); toast.success('Marked on its way — client told'); onDone(); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Send ${o.ref}`} size="md">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">The client sees the driver&apos;s name, phone and vehicle. Trip cost stays private.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input label="Driver name" value={f.driverName} onChange={set('driverName')} />
          <Input label="Driver phone" value={f.driverPhone} onChange={set('driverPhone')} placeholder="+243…" />
          <Input label="Vehicle" value={f.vehicle} onChange={set('vehicle')} placeholder="e.g. moto, camionnette" />
          <Input label="Plate" value={f.plate} onChange={set('plate')} />
          <Input label="Trip cost (USD, private)" inputMode="decimal" value={f.tripCost} onChange={set('tripCost')} />
          <Input label="Note (private)" value={f.note} onChange={set('note')} />
        </div>
        <div className="flex gap-2"><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={save}>Mark on its way</Button></div>
      </div>
    </Modal>
  );
}

// Amount, currency, country, client and reference come from the order and
// cannot be edited — the server refuses anything that differs.
function RecordPaymentModal({ o, onClose, onDone }) {
  const [f, setF] = useState({ method: 'bank', providerRef: '', receivedInto: '', payerName: o.buyer?.name || o.buyerName || '', payerPhone: o.phone || '',
    paidOn: new Date().toISOString().slice(0, 10), proofNote: '' });
  const [saving, setSaving] = useState(false);
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!window.confirm(`Record ${usd(o.totalMinor, o.currency)} received for ${o.ref}? It counts as paid only after a DIFFERENT approver approves it.`)) return;
    setSaving(true);
    try {
      await dwisendPaymentAPI.record({ ...f, subjectType: 'dwisend_order', subjectId: o._id, subjectRef: o.ref,
        countryCode: o.countryCode, currency: o.currency, amountMinor: o.totalMinor, customerId: o.buyerId });
      toast.success('Recorded — waiting for a different approver');
      onDone();
    } catch (err) {
      toast.error(err.status === 503 ? 'Payment approvals are switched OFF — nothing can be recorded until the owner turns them on' : err.message);
    } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Record payment — ${o.ref}`} size="md">
      <div className="space-y-3">
        <div className="rounded-xl bg-bg-elevated p-3 text-sm">
          <div>Amount: <b className="text-text-primary">{usd(o.totalMinor, o.currency)}</b> (exactly — part payments are not supported)</div>
          <div className="text-xs text-text-muted">Check this against the real bank / mobile money statement, not a screenshot.</div>
        </div>
        <Select label="How was it paid?" value={f.method} onChange={set('method')}>
          <option value="bank">Bank</option><option value="mobile_money">Mobile money</option>
          <option value="cash_deposit">Cash deposit</option><option value="other">Other</option>
        </Select>
        <Input label="Transaction reference (from the statement)" value={f.providerRef} onChange={set('providerRef')} />
        <Input label="Received into (which DWI account)" value={f.receivedInto} onChange={set('receivedInto')} placeholder="e.g. Equity BCDC USD ****1234" />
        <div className="grid sm:grid-cols-2 gap-2">
          <Input label="Payer name (as on statement)" value={f.payerName} onChange={set('payerName')} />
          <Input label="Payer phone" value={f.payerPhone} onChange={set('payerPhone')} />
        </div>
        <Input label="Date on the statement" type="date" value={f.paidOn} onChange={set('paidOn')} />
        <Input label="Proof note (optional)" value={f.proofNote} onChange={set('proofNote')} />
        <div className="flex gap-2"><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={save}>Record payment</Button></div>
      </div>
    </Modal>
  );
}

// ── Products ─────────────────────────────────────────────────────────────────
function ProductsPanel() {
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [units, setUnits] = useState([]);
  const [editing, setEditing] = useState(null);   // product or {} for new
  const [stockFor, setStockFor] = useState(null);
  const load = useCallback(async () => {
    try {
      const [p, c, u] = await Promise.all([dwisendShopAPI.adminProducts(), marketAPI.listCatalog('category'), marketAPI.listCatalog('unit')]);
      setData(p); setCats(c.items || []); setUnits(u.items || []);
    } catch (err) { toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="py-10 flex justify-center"><Spinner /></div>;
  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <p className="text-xs text-text-muted">Categories and sizes are shared with Uganda (DwiSend → Catalog). Prices here are USD and only for DRC.
          To sell a Uganda product here too, use &quot;🇨🇩 Also sell in DRC shop&quot; on it under Products &amp; Stock.</p>
        <Button size="sm" onClick={() => setEditing({})}>+ New DRC product</Button>
      </div>
      {data.products.length === 0 ? <Empty title="No DRC products yet" description="Add the first product clients in DRC can buy." /> : (
        <Table headers={['Product', 'Category', 'Sizes (price · cost · in stock)', 'Stock', 'Shown']}>
          {data.products.map(p => (
            <tr key={p._id} className="hover:bg-bg-elevated">
              <Td className="text-text-primary"><button className="text-left font-semibold hover:text-cyan" onClick={() => setEditing(p)}>{p.name}</button>
                {p.linked && <div className="text-[11px] text-cyan">🇺🇬 shared from Uganda: {p.linked.missing ? 'product not found' : p.linked.name}{p.linked.isActive === false ? ' (hidden there)' : ''}</div>}
                {p.supplier?.name && <div className="text-[11px] text-text-muted">from {p.supplier.name}</div>}</Td>
              <Td>{p.categoryName}{!p.categoryActive && <div className="text-[11px] text-amber">category hidden</div>}</Td>
              <Td>{p.variants.map(v => (
                <div key={v._id} className={`text-xs ${v.isActive ? '' : 'line-through text-text-muted'}`}>
                  {v.unitName}: {usd(v.priceMinor, p.currency)}{v.priceAuto ? ' (auto)' : ''} · {usd(v.costMinor, p.currency)}{v.marginOnCostPct != null ? ` · +${v.marginOnCostPct}%` : ''} · {v.inStock}{v.weightGrams != null ? ` · ${kgText(v.weightGrams)}` : ''}
                </div>
              ))}</Td>
              <Td>{stockEquivalent(p.stockQty, p.baseUnit, p.variants.map(v => ({ name: v.unitName, baseQty: v.baseQty })))} <Button size="xs" variant="ghost" onClick={() => setStockFor(p)}>Change</Button></Td>
              <Td>{p.isActive ? 'Yes' : 'Hidden'}</Td>
            </tr>
          ))}
        </Table>
      )}
      {editing && <ProductModal p={editing} cats={cats} units={units} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onStockChanged={load} />}
      {stockFor && <StockModal p={stockFor} onClose={() => setStockFor(null)} onSaved={() => { setStockFor(null); load(); }} />}
    </>
  );
}

// p = a DRC product, {} for a new own product, or { linkedFrom: <Uganda product> }
// to list a Uganda product here too. A linked listing shares Uganda's photos,
// description, details and category (edited on the Uganda product); it keeps its
// own USD cost/price, stock, weight, supplier and shown/hidden, and may have a
// French name / description.
export function ProductModal({ p, cats, units, onClose, onSaved, onStockChanged }) {
  const isNew = !p._id;
  const ug = p.linkedFrom;
  const linked = ug
    ? { productId: ug._id, name: ug.name, description: ug.description || '', isActive: ug.isActive !== false,
      costs: (ug.variants || []).map(v => ({ unitId: v.unitId, costUGX: v.costUGX ?? null })) }
    : p.linked || null;
  const photosShown = ug ? ug.photos || [] : p.photos || [];
  const [settings, setSettings] = useState(null);
  const [f, setF] = useState(() => ({
    name: p.name || '', description: p.description || '', categoryId: p.categoryId || cats.find(c => c.isActive !== false)?._id || '',
    nameFr: p.nameFr || '', descriptionFr: p.descriptionFr || '',
    photos: p.photos || [], isActive: ug ? false : p.isActive !== false, marginPct: p.marginPct ?? '', stockQty: '',
    details: { brand: '', condition: '', warranty: '', origin: '', ...(p.details || {}) },
    supplier: { name: '', phone: '', whatsapp: '', location: '', notes: '', ...(p.supplier || {}) },
    variants: ug
      // Uganda's sizes, ready for a USD cost each (weight and price are this listing's own)
      ? (ug.variants || []).filter(v => v.isActive !== false).map(v => ({ unitId: v.unitId, price: '', cost: '', priceAuto: true, minOrderQty: String(v.minOrderQty || 1), weightKg: '', isActive: true }))
      : (p.variants || []).map(v => ({ _id: v._id, unitId: v.unitId, price: asText(v.priceMinor), cost: asText(v.costMinor), priceAuto: !!v.priceAuto, minOrderQty: String(v.minOrderQty || 1),
        weightKg: v.weightGrams == null ? '' : String(v.weightGrams / 1000), isActive: v.isActive !== false, costConversion: v.costConversion || null })),
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [converting, setConverting] = useState(null);   // { i, suggestion } — shown, not saved
  const [stock, setStock] = useState(isNew ? null : p);   // saved listing's stock — changed only through the Stock action
  const [stockOpen, setStockOpen] = useState(false);
  useEffect(() => { dwisendShopAPI.adminSettings().then(r => setSettings(r.settings)).catch(() => {}); }, []);
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const setIn = (g, k) => e => setF(x => ({ ...x, [g]: { ...x[g], [k]: e.target.value } }));
  const setV = (i, k, v) => setF(x => ({ ...x, variants: x.variants.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const addSize = () => setF(x => ({ ...x, variants: [...x.variants, { unitId: units.find(u => u.isActive !== false)?._id || '', price: '', cost: '', priceAuto: !!linked, minOrderQty: '1', weightKg: '', isActive: true }] }));
  // A hint only (e.g. "50" for a 50 kg bag) — only what is typed is saved.
  const weightHint = unitId => { const u = units.find(x => x._id === unitId); return u?.baseUnit === 'kg' ? `e.g. ${u.baseQty}` : 'kg'; };
  const ugCost = unitId => linked?.costs?.find(c => String(c.unitId) === String(unitId))?.costUGX ?? null;
  // Sizes picked in the form, for "500 kg = 50 × 10kg"
  const pickedUnits = f.variants.map(v => units.find(u => String(u._id) === String(v.unitId))).filter(Boolean);
  const baseUnit = pickedUnits[0]?.baseUnit || p.baseUnit || '';
  const savedStock = stock && stockEquivalent(stock.stockQty, stock.baseUnit, (stock.variants || []).map(v => ({ name: v.unitName, baseQty: v.baseQty })));
  const [stockHead, stockSizes] = savedStock ? savedStock.split(' = ') : [];
  const upload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const r = await marketAPI.uploadPhoto(file); setF(x => ({ ...x, photos: [...x.photos, r.url].slice(0, 6) })); }
    catch (err) { toast.error(err.message); }
    finally { setUploading(false); e.target.value = ''; }
  };
  const convert = async i => {
    try {
      const { suggestion } = await dwisendShopAPI.adminConvert({ marketProductId: linked.productId, unitId: f.variants[i].unitId, marginPct: f.marginPct });
      setConverting({ i, suggestion });
    } catch (err) { toast.error(err.message); }
  };
  // "Use": the suggestion goes into the cost box only — still editable, saved only with Save.
  const useSuggestion = () => {
    const { i, suggestion: sg } = converting;
    setF(x => ({ ...x, variants: x.variants.map((r, j) => (j === i
      ? { ...r, cost: asText(sg.suggestedMinor), conversion: { ugx: sg.ugx, ugxPerUsd: sg.ugxPerUsd, suggestedMinor: sg.suggestedMinor } } : r)) }));
    setConverting(null);
  };
  const save = async () => {
    setSaving(true);
    const body = { ...f, variants: f.variants.map(({ costConversion, ...v }) => v) };
    if (!isNew) delete body.stockQty;
    if (linked) { for (const k of ['name', 'description', 'categoryId', 'photos', 'details']) delete body[k]; }
    else { delete body.nameFr; delete body.descriptionFr; }
    if (ug) body.linkedMarketProductId = ug._id;
    try {
      if (isNew) await dwisendShopAPI.adminCreateProduct(body); else await dwisendShopAPI.adminUpdateProduct(p._id, body);
      toast.success('Saved'); onSaved();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  const title = ug ? `List in DRC shop — ${ug.name}` : isNew ? 'New DRC product' : `Edit — ${p.name}`;
  return (
    <Modal open onClose={onClose} title={title} size="lg">
      <div className="space-y-3">
        {linked ? (
          <div className="rounded-xl border border-border p-3 space-y-2">
            <div className="text-xs font-semibold text-text-secondary">Shared from the Uganda product &quot;{linked.name}&quot;{linked.isActive === false ? ' (hidden in Uganda — still sold here)' : ''}</div>
            {photosShown.length > 0 && <div className="flex gap-2 flex-wrap">{photosShown.map(u => <img key={u} src={u} alt="" className="w-16 h-16 object-cover rounded-lg" />)}</div>}
            {linked.description && <p className="text-xs text-text-muted whitespace-pre-line">{linked.description}</p>}
            <p className="text-[11px] text-text-muted">Photos, description, details and category come from Uganda — change them on the Uganda product and they change here too.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Input label="French name (optional)" value={f.nameFr} onChange={set('nameFr')} placeholder={linked.name} />
              <div className="text-[11px] text-text-muted self-end pb-2">Blank = clients see the Uganda name.</div>
            </div>
            <div>
              <label className="text-xs font-semibold text-text-secondary">French description (optional — blank shows the Uganda description)</label>
              <textarea rows={2} maxLength={2000} value={f.descriptionFr} onChange={set('descriptionFr')} className={textarea} />
            </div>
          </div>
        ) : (
          <>
            <p className="text-xs text-text-muted">Name, description and details are shown to DRC clients exactly as typed — write them in French. Cost and supplier are private.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Input label="Name (as clients see it)" value={f.name} onChange={set('name')} placeholder="ex. Riz long grain" />
              <Select label="Category" value={f.categoryId} onChange={set('categoryId')}>
                {cats.map(c => <option key={c._id} value={c._id}>{c.name}{c.isActive === false ? ' (hidden)' : ''}</option>)}
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text-secondary">Description (shown to clients)</label>
              <textarea rows={2} maxLength={2000} value={f.description} onChange={set('description')} className={textarea} />
            </div>
            <div className="grid sm:grid-cols-4 gap-2">
              <Input label="Brand" value={f.details.brand} onChange={setIn('details', 'brand')} />
              <Input label="Origin" value={f.details.origin} onChange={setIn('details', 'origin')} />
              <Input label="Warranty" value={f.details.warranty} onChange={setIn('details', 'warranty')} />
              <Select label="Condition" value={f.details.condition} onChange={setIn('details', 'condition')}>
                <option value="">—</option><option value="new">New</option><option value="used">Used</option><option value="refurbished">Refurbished</option>
              </Select>
            </div>
            <div>
              <div className="text-xs font-semibold text-text-secondary mb-1">Photos ({f.photos.length}/6)</div>
              <div className="flex gap-2 flex-wrap items-center">
                {f.photos.map((u, i) => (
                  <div key={u} className="relative"><img src={u} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    <button className="absolute -top-1 -right-1 bg-red text-white rounded-full w-5 h-5 text-xs" onClick={() => setF(x => ({ ...x, photos: x.photos.filter((_, j) => j !== i) }))}>×</button></div>
                ))}
                {f.photos.length < 6 && <label className="text-xs text-cyan underline cursor-pointer">{uploading ? 'Uploading…' : '+ Add photo'}<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} /></label>}
              </div>
            </div>
          </>
        )}
        <div>
          <div className="text-sm font-bold text-text-primary">Sizes (USD)</div>
          <p className="text-[11px] text-text-muted">Weight (kg) is optional and never shown to clients — you weigh each package and set the delivery fee on the order. Leave it blank.</p>
          {f.variants.map((v, i) => {
            const preview = autoPreview(v, f.marginPct, settings);
            return (
              <div key={i} className="mt-2 rounded-lg border border-border/60 p-2">
                <div className="grid grid-cols-2 sm:grid-cols-8 gap-2 items-end">
                  <Select label="Size" value={v.unitId} onChange={e => setV(i, 'unitId', e.target.value)} className="col-span-2">
                    {units.map(u => <option key={u._id} value={u._id}>{u.name}{u.isActive === false ? ' (hidden)' : ''}</option>)}
                  </Select>
                  <Input label="Price" inputMode="decimal" value={v.priceAuto ? (preview != null ? asText(preview) : v.price) : v.price} disabled={v.priceAuto} onChange={e => setV(i, 'price', e.target.value)} />
                  <Input label="Cost (private)" inputMode="decimal" value={v.cost} onChange={e => setV(i, 'cost', e.target.value)} />
                  <Input label="Min qty" type="number" min="1" step="1" value={v.minOrderQty} onChange={e => setV(i, 'minOrderQty', e.target.value)} />
                  <Input label="Weight (kg)" inputMode="decimal" value={v.weightKg} placeholder={weightHint(v.unitId)} onChange={e => setV(i, 'weightKg', e.target.value)} />
                  <label className="text-xs flex items-center gap-1 pb-2"><input type="checkbox" checked={v.priceAuto} onChange={e => setV(i, 'priceAuto', e.target.checked)} /> cost + margin</label>
                  <div className="flex gap-2 pb-2 text-xs">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={v.isActive} onChange={e => setV(i, 'isActive', e.target.checked)} /> on sale</label>
                    {!v._id && <button className="text-red underline" onClick={() => setF(x => ({ ...x, variants: x.variants.filter((_, j) => j !== i) }))}>remove</button>}
                  </div>
                </div>
                {linked && (
                  <div className="flex flex-wrap gap-2 items-center mt-1 text-[11px] text-text-muted">
                    <span>Uganda cost for this size: {ugCost(v.unitId) == null ? 'none' : ugxText(ugCost(v.unitId))}</span>
                    <Button size="xs" variant="secondary" disabled={ugCost(v.unitId) == null || !settings?.ugxPerUsd} onClick={() => convert(i)}
                      title={!settings?.ugxPerUsd ? 'Set "UGX per 1 USD" in Settings first' : undefined}>⇄ Convert from UGX</Button>
                    {settings && !settings.ugxPerUsd && <span>Set the rate in Settings first</span>}
                  </div>
                )}
                {v.priceAuto && preview != null && <div className="text-[11px] text-text-muted mt-1">{priceNote(v, f.marginPct, settings)}</div>}
                <ConversionNote v={v} />
              </div>
            );
          })}
          <Button size="xs" variant="secondary" className="mt-2" onClick={addSize}>+ Add size</Button>
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          <Input label="Margin % for this product (optional)" value={f.marginPct} onChange={set('marginPct')} placeholder="default from Settings" />
          {isNew ? (
            <div>
              <Input label={`${linked ? 'DRC stock' : 'Starting stock'} (in ${baseUnit || 'the base unit'}${linked ? ' — Uganda stock is separate' : ''})`}
                type="number" min="0" step="1" value={f.stockQty} onChange={set('stockQty')} placeholder={baseUnit ? `e.g. 500 ${baseUnit}` : ''} />
              {f.stockQty !== '' && <div className="text-[11px] text-text-muted mt-1">{stockEquivalent(f.stockQty, baseUnit, pickedUnits) || 'Whole numbers only'}</div>}
            </div>
          ) : (
            <div className="text-sm self-end pb-2">
              <span className="text-text-secondary">{linked ? 'DRC stock' : 'Stock'}:</span>{' '}
              <span className="font-semibold text-text-primary tabular-nums">{stockHead}</span>
              {stockSizes && <span className="text-text-muted"> (= {stockSizes})</span>}{' '}
              <Button size="xs" variant="secondary" onClick={() => setStockOpen(true)}>Change</Button>
            </div>
          )}
          <label className="text-sm flex items-center gap-2 pb-2"><input type="checkbox" checked={f.isActive} onChange={e => setF(x => ({ ...x, isActive: e.target.checked }))} /> Shown in the DRC shop</label>
        </div>
        <div className="rounded-xl border border-border p-3">
          <div className="text-xs font-semibold text-text-secondary mb-1">Supplier (private — clients never see this)</div>
          <div className="grid sm:grid-cols-4 gap-2">
            <Input label="Name" value={f.supplier.name} onChange={setIn('supplier', 'name')} />
            <Input label="Phone" value={f.supplier.phone} onChange={setIn('supplier', 'phone')} />
            <Input label="WhatsApp" value={f.supplier.whatsapp} onChange={setIn('supplier', 'whatsapp')} placeholder="243…" />
            <Input label="Location" value={f.supplier.location} onChange={setIn('supplier', 'location')} />
          </div>
        </div>
        <div className="flex gap-2"><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={save}>Save</Button></div>
      </div>
      {!isNew && <ChangeHistory productId={p._id} refreshKey={stock?.stockQty} />}
      {converting && <ConvertPanel sg={converting.suggestion} onUse={useSuggestion} onCancel={() => setConverting(null)} />}
      {stockOpen && <StockModal p={stock} onClose={() => setStockOpen(false)}
        onSaved={updated => { setStockOpen(false); if (updated) setStock(updated); onStockChanged?.(); }} />}
    </Modal>
  );
}

// Every change to this listing, one row per field (from the audit log): when,
// who, admin screen or Claude Code script, old → new.
function ChangeHistory({ productId, refreshKey }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!open) return;
    dwisendShopAPI.adminProductHistory(productId).then(r => setRows(r.history)).catch(err => toast.error(err.message));
  }, [open, productId, refreshKey]);
  return (
    <div className="mt-4 border-t border-border pt-3">
      <button type="button" className="text-xs font-semibold text-text-secondary hover:text-cyan" onClick={() => setOpen(o => !o)}>{open ? '▾' : '▸'} Change history</button>
      {open && (rows == null ? <div className="py-3"><Spinner /></div> : rows.length === 0 ? <div className="text-xs text-text-muted mt-2">No changes recorded.</div> : (
        <div className="mt-2 space-y-1 text-xs max-h-64 overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i}>
              <span className="text-text-muted">{when(r.at)} · {r.by} ({r.via === 'claude-code' ? 'Claude Code script' : 'admin screen'})</span>{' '}
              <span className="text-text-secondary">{r.field}:</span> {r.from} → <b className="text-text-primary">{r.to}</b>{r.note ? <span className="text-text-muted"> · {r.note}</span> : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// The same rule as the server (cost + margin, rounded UP) — a preview; the server prices on Save.
export function autoPreview(v, productMarginPct, settings) {
  if (!settings || !v.priceAuto) return null;
  const cost = Math.round(Number(String(v.cost).replace(',', '.')) * 100);
  const margin = productMarginPct === '' || productMarginPct == null ? settings.defaultMarginPct : Number(String(productMarginPct).replace(',', '.'));
  if (!Number.isFinite(cost) || cost <= 0 || margin == null || !Number.isFinite(margin)) return null;
  const round = settings.priceRoundMinor || 1;
  return Math.ceil((cost * (100 + margin)) / 100 / round - 1e-9) * round;
}

function priceNote(v, productMarginPct, settings) {
  const margin = productMarginPct === '' || productMarginPct == null ? settings.defaultMarginPct : productMarginPct;
  return `Price ${usd(autoPreview(v, productMarginPct, settings))} = cost + ${margin}%, rounded up to ${asText(settings.priceRoundMinor)}`;
}

// What the saved (or about-to-be-saved) conversion says, so the rate used stays visible.
function ConversionNote({ v }) {
  const c = v.conversion || v.costConversion;
  if (!c) return null;
  const typed = Math.round(Number(String(v.cost).replace(',', '.')) * 100);
  const changed = Number.isFinite(typed) && typed !== c.suggestedMinor;
  return (
    <div className="text-[11px] text-cyan mt-1">
      {v.conversion ? 'Suggested' : 'Converted'} from {ugxText(c.ugx)} at 1 USD = {rateText(c.ugxPerUsd)} UGX{c.at ? ` on ${when(c.at)}` : ''}
      {changed ? ` · suggested ${asText(c.suggestedMinor)}, you set ${String(v.cost)}` : ''}
      {v.conversion ? ' — saved when you press Save' : ''}
    </div>
  );
}

// Shows the suggestion. Nothing is saved: "Use" only fills the cost box.
// On document.body so it sits above the product form, not inside its scroll area.
function ConvertPanel({ sg, onUse, onCancel }) {
  return createPortal(
    <Modal open onClose={onCancel} title="Suggested USD cost" size="sm">
      <div className="space-y-2 text-sm">
        <Row label="Uganda cost for this size" value={ugxText(sg.ugx)} />
        <Row label="Rate used" value={`1 USD = ${rateText(sg.ugxPerUsd)} UGX`} />
        <div className="text-[11px] text-text-muted text-right -mt-1">set {sg.rateSetBy ? `by ${sg.rateSetBy} ` : ''}{when(sg.rateSetAt)}</div>
        <Row label="Calculation" value={`${Number(sg.ugx).toLocaleString('en-US')} ÷ ${rateText(sg.ugxPerUsd)} = ${(sg.ugx / sg.ugxPerUsd).toFixed(3)}`} />
        <Row label="Suggested cost (rounded up to the cent)" value={usd(sg.suggestedMinor, sg.currency)} strong />
        <Row label="DRC price would be" value={sg.priceMinor == null ? 'set a margin first' : `${usd(sg.priceMinor, sg.currency)} (cost + ${sg.marginPct}%, up to ${asText(sg.roundMinor)})`} />
        <p className="text-[11px] text-text-muted">Nothing is saved yet. &quot;Use&quot; puts the number in the cost box — you can still change it, and it is saved only when you press Save.</p>
        <div className="flex gap-2 pt-1"><Button onClick={onUse}>Use {usd(sg.suggestedMinor, sg.currency)}</Button><Button variant="secondary" onClick={onCancel}>Cancel</Button></div>
      </div>
    </Modal>,
    document.body,
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-text-muted">{label}</span>
      <span className={`text-right tabular-nums ${strong ? 'font-bold text-text-primary' : 'text-text-primary'}`}>{value}</span>
    </div>
  );
}

// On a Uganda product (Admin → DwiSend → Products): list it in the DRC shop too,
// or open its DRC listing. Admin screen only — the Uganda shop is not affected.
export function DrcListingButton({ product, listing, units, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {listing
        ? <Button size="xs" variant="ghost" onClick={() => setOpen(true)} title="Open its DRC listing">🇨🇩 In DRC shop{listing.isActive ? '' : ' (hidden)'}</Button>
        : <Button size="xs" variant="ghost" onClick={() => setOpen(true)}>🇨🇩 Also sell in DRC shop</Button>}
      {open && <ProductModal p={listing || { linkedFrom: product }} cats={[]} units={units} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); onSaved?.(); }} onStockChanged={onSaved} />}
    </>
  );
}

// Opened from the products list or from the listing's edit form (so it goes on
// document.body, above the form). onSaved gets the updated product.
function StockModal({ p, onClose, onSaved }) {
  const [mode, setMode] = useState('add');
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const sizes = (p.variants || []).map(v => ({ name: v.unitName, baseQty: v.baseQty }));
  const typed = Number(qty);
  const result = qty === '' || !Number.isInteger(typed) ? null : mode === 'set' ? typed : p.stockQty + typed;
  const save = async () => {
    setSaving(true);
    try { const r = await dwisendShopAPI.adminStock(p._id, { mode, qty: Number(qty), note }); toast.success('Stock saved'); onSaved(r.product); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  return createPortal(
    <Modal open onClose={onClose} title={`Stock — ${p.name} (now ${stockEquivalent(p.stockQty, p.baseUnit, sizes)})`} size="sm">
      <div className="space-y-3">
        <Select label="Change" value={mode} onChange={e => setMode(e.target.value)}>
          <option value="add">Add or remove (+ delivery in, − loss)</option><option value="set">Set to a counted amount</option>
        </Select>
        <Input label={`Quantity (${p.baseUnit})`} type="number" step="1" value={qty} onChange={e => setQty(e.target.value)} />
        {qty !== '' && <div className="text-[11px] text-text-muted -mt-2">
          {result == null ? 'Whole numbers only'
            : result < 0 ? 'Stock cannot go below 0'
            : `${mode === 'add' ? `${typed < 0 ? '−' : '+'}${stockEquivalent(Math.abs(typed), p.baseUnit, sizes)} → ` : ''}stock becomes ${stockEquivalent(result, p.baseUnit, sizes)}`}
        </div>}
        <Input label="Reason" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. delivery from supplier, stock count" />
        <div className="flex gap-2"><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={save}>Save</Button></div>
      </div>
    </Modal>,
    document.body,
  );
}

// ── Settings ─────────────────────────────────────────────────────────────────
function SettingsPanel() {
  const [s, setS] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const { settings } = await dwisendShopAPI.adminSettings();
      setS(settings);
      setF({ defaultMarginPct: settings.defaultMarginPct ?? '', priceRound: asText(settings.priceRoundMinor), paymentHoldHours: String(settings.paymentHoldHours),
        minOrder: settings.minOrderMinor ? asText(settings.minOrderMinor) : '', maxUnpaidOrders: String(settings.maxUnpaidOrders),
        maxHoldDays: String(settings.maxHoldDays ?? 7), ugxPerUsd: settings.ugxPerUsd == null ? '' : String(settings.ugxPerUsd) });
    } catch (err) { toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!s || !f) return <div className="py-10 flex justify-center"><Spinner /></div>;
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const r = await dwisendShopAPI.adminSaveSettings({
        defaultMarginPct: f.defaultMarginPct, priceRound: f.priceRound, paymentHoldHours: Number(f.paymentHoldHours),
        minOrder: f.minOrder, maxUnpaidOrders: Number(f.maxUnpaidOrders), maxHoldDays: Number(f.maxHoldDays), ugxPerUsd: f.ugxPerUsd,
      });
      toast.success(r.repriced ? `Saved — ${r.repriced} auto-priced product(s) re-priced` : 'Saved');
      load();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      {!s.paymentInstructionsSet && <div className="text-xs text-amber">No payment details are set on the server (DWISEND_CD_PAY_1…6 in .env) — clients are told to wait for details.</div>}
      <div className="grid sm:grid-cols-3 gap-2">
        <Input label="Default margin % (cost + margin pricing)" value={f.defaultMarginPct} onChange={set('defaultMarginPct')} placeholder="e.g. 15" />
        <Input label={`Round auto prices up to (${s.currency})`} inputMode="decimal" value={f.priceRound} onChange={set('priceRound')} />
        <Input label="Payment window (hours — restarts when the delivery fee is set)" type="number" min="1" max="168" value={f.paymentHoldHours} onChange={set('paymentHoldHours')} />
        <Input label="Longest hold (days from placing the order)" type="number" min="1" max="30" value={f.maxHoldDays} onChange={set('maxHoldDays')} />
        <Input label={`Minimum order (${s.currency}, optional)`} inputMode="decimal" value={f.minOrder} onChange={set('minOrder')} />
        <Input label="Unpaid orders allowed per client" type="number" min="1" max="20" value={f.maxUnpaidOrders} onChange={set('maxUnpaidOrders')} />
      </div>
      <div>
        <div className="text-sm font-bold text-text-primary">Convert from UGX</div>
        <p className="text-xs text-text-muted">The rate the &quot;Convert from UGX&quot; button uses on products listed from Uganda. It only suggests a USD cost —
          you see the number and confirm or change it before saving. Changing the rate never changes a saved price.</p>
        <div className="grid sm:grid-cols-3 gap-2 mt-2 items-end">
          <Input label="UGX per 1 USD" inputMode="decimal" value={f.ugxPerUsd} onChange={set('ugxPerUsd')} placeholder="e.g. 3700" />
          <div className="text-xs text-text-muted pb-2 sm:col-span-2">
            {s.ugxPerUsd ? `Now: 1 USD = ${rateText(s.ugxPerUsd)} UGX · set ${s.ugxPerUsdSetByName ? `by ${s.ugxPerUsdSetByName} ` : ''}${when(s.ugxPerUsdSetAt)}` : 'Not set — the Convert button stays off until you set it.'}
          </div>
        </div>
      </div>
      <div>
        <div className="text-sm font-bold text-text-primary">Delivery</div>
        <p className="text-xs text-text-muted">Negotiated per order (weight, town, items) — clients see &quot;À confirmer&quot; and you set the fee on each order under Orders.
          Clients can order to every active city: {s.cities.filter(c => c.isActive).map(c => c.name).join(', ') || 'none yet'} (managed under 🏙️ Cities).</p>
      </div>
      <Button loading={saving} onClick={save}>Save settings</Button>
    </div>
  );
}
